import type { IconElement } from '@backstage/frontend-plugin-api';
import type { IconProps } from '@mui/material';
import type { ComponentType, FC } from 'react';

import { iconsApiRef, useApi } from '@backstage/frontend-plugin-api';
import { isValidElement, useRef } from 'react';

import type { Icon } from '../types';

type Props = {
  icon?: Icon;
  size?: number;
};

export const ResolvedIcon: FC<Props> = ({ icon, size = 32 }) => {
  const iconsApi = useApi(iconsApiRef);

  const cacheRef = useRef<Map<string, IconElement | null>>(new Map());

  if (!icon) return null;

  /**
   * 1. string → resolve via Backstage API or fallback to <img />
   */
  if (typeof icon === 'string') {
    if (!cacheRef.current.has(icon)) {
      const resolved = iconsApi.icon(icon);
      cacheRef.current.set(icon, resolved ?? null);
    }

    const resolvedIcon = cacheRef.current.get(icon);

    return (
      resolvedIcon ?? <img alt="icon" height={size} src={icon} width={size} />
    );
  }

  /**
   * 2. already a React element → return directly
   */
  if (isValidElement(icon)) {
    return icon;
  }

  /**
   * 3. component type (MUI icon or custom component)
   */
  const IconComponent = icon as ComponentType<IconProps>;

  return <IconComponent />;
};
